import React from "react"

/**
 * 通用进度条组件
 * 
 * @example
 * // 不确定进度的加载条
 * <ProgressBar visible={loading} animation="shimmer" />
 * 
 * // 确定进度的进度条
 * <ProgressBar progress={75} color="green" height="md" />
 * 
 * // 用于标签页顶部的细线进度条
 * <ProgressBar 
 *   visible={loading}
 *   height="sm"
 *   className="absolute top-0 left-0 right-0"
 * />
 */
interface ProgressBarProps {
  /** 是否显示进度条 */
  visible?: boolean
  /** 进度条高度 */
  height?: "xs" | "sm" | "md" | "lg"
  /** 进度条颜色 */
  color?: "blue" | "green" | "red" | "yellow" | "purple"
  /** 动画类型 */
  animation?: "pulse" | "shimmer" | "none"
  /** 自定义样式类 */
  className?: string
  /** 进度值 (0-100)，如果提供则显示确定进度 */
  progress?: number
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  visible = true,
  height = "xs",
  color = "blue",
  animation = "shimmer",
  className = "",
  progress
}) => {
  if (!visible) return null

  const heightClasses = {
    xs: "h-0.5",
    sm: "h-1",
    md: "h-2",
    lg: "h-3"
  }

  const colorClasses = {
    blue: "bg-blue-600",
    green: "bg-green-600", 
    red: "bg-red-600",
    yellow: "bg-yellow-600",
    purple: "bg-purple-600"
  }

  const backgroundColorClasses = {
    blue: "bg-blue-100",
    green: "bg-green-100",
    red: "bg-red-100", 
    yellow: "bg-yellow-100",
    purple: "bg-purple-100"
  }

  const shimmerColorClasses = {
    blue: "from-transparent via-blue-400 to-transparent",
    green: "from-transparent via-green-400 to-transparent",
    red: "from-transparent via-red-400 to-transparent",
    yellow: "from-transparent via-yellow-400 to-transparent",
    purple: "from-transparent via-purple-400 to-transparent"
  }

  const animationClasses = {
    pulse: "animate-pulse",
    shimmer: "",
    none: ""
  }

  const isIndeterminate = progress === undefined

  return (
    <div className={`relative ${heightClasses[height]} ${backgroundColorClasses[color]} rounded-full overflow-hidden ${className}`}>
      {/* 进度条填充 */}
      <div 
        className={`${heightClasses[height]} ${colorClasses[color]} rounded-full transition-all duration-300 ${
          isIndeterminate ? "w-full" : ""
        } ${animationClasses[animation]}`}
        style={!isIndeterminate ? { width: `${Math.min(100, Math.max(0, progress))}%` } : undefined}
      />
      
      {/* 闪光动画效果 - 仅在不确定进度时显示 */}
      {isIndeterminate && animation === "shimmer" && (
        <div className={`absolute top-0 left-0 ${heightClasses[height]} w-full bg-gradient-to-r ${shimmerColorClasses[color]} animate-shimmer`} />
      )}
    </div>
  )
}

export default ProgressBar